import { useTranslator } from '@bluebeam/react-i18n';
import { Button } from 'components/lib';
import {
  MapOverlayMachineEventsEnum,
  useMapOverlayMachine,
} from 'components/Overlays/MapOverlayMachine';
import AppStoreContext from 'contexts/AppStoreContext';
import Immutable from 'immutable';
import React, { FC, useContext } from 'react';
import { useLocation } from 'react-router-dom';
import { PostRFI } from 'records/interactions/PostRFI';
import { ViewRFI } from 'records/interactions/ViewRFI';

const PostRFIFooter: FC = () => {
  const { state: appState, actions } = useContext(AppStoreContext);
  const { interactions } = appState;
  const { mapOverlayMachineService } = useMapOverlayMachine();
  const location = useLocation();
  const { translate } = useTranslator(['buttons']);

  const rfiPostInteraction = interactions.find(
    (i) => i.type === 'PostRFI'
  ) as PostRFI;

  const { markup, rfi, updatingMarkup, originalMarkup, removingMarkup } =
    rfiPostInteraction;
  async function handlePostRFICancelClick() {
    actions.deleteInteractions('PostRFI');

    if (updatingMarkup) {
      if (!originalMarkup) return;

      actions.setLocalMarkup(originalMarkup);

      return;
    }

    // new PostRFI logic
    if (markup !== null) {
      actions.removeLocalMarkup(markup);
    }
  }

  async function handlePostRFIConfirmClick() {
    let shouldOpenRFIOverlay = true;

    actions.deleteInteractions('PostRFI');

    if (updatingMarkup) {
      if (!originalMarkup) return;

      if (markup) {
        if (markup.markupid === originalMarkup.markupid) {
          await actions.updateMarkup(markup);
        } else {
          await actions.deleteMarkup(originalMarkup);
          await actions.postMarkupsToRFI(rfi, Immutable.List([markup]));
        }
      } else {
        // markup has been removed/deleted.
        actions.deleteMarkup(originalMarkup);
        shouldOpenRFIOverlay = false;
      }
    } else if (markup !== null) {
      // An RFI can be posted if it has an answer OR a previous version
      if (rfi.answer || rfi.previousversionid) {
        await actions.postMarkupsToRFI(rfi, Immutable.List([markup]));
      }
    }

    if (shouldOpenRFIOverlay) {
      const viewRFIInteraction = new ViewRFI({
        backArrow: true,
        containerStyles: {
          top: 96,
          height: `calc(100vh - 96px)`,
        },
        markup,
        placing: true,
        rfi,
      });

      if (location.pathname.includes('map')) {
        mapOverlayMachineService.send({
          type: MapOverlayMachineEventsEnum.OPEN_RFI_OVERLAY,
          data: {
            interaction: viewRFIInteraction,
          },
        });
      } else {
        actions.createInteraction(viewRFIInteraction);
      }
    }
  }

  function confirmBtnDisabled(): boolean {
    if (updatingMarkup) {
      // Disable if markup has not been changed.
      return markup === originalMarkup;
    }

    // Disable if no markup has been drawn.
    return markup === null;
  }

  return (
    <div
      className="hrem-4p5 w100 bgd-white-2 px4 row justify-end items-center"
      style={{ position: 'sticky', bottom: '0' }}
    >
      <Button
        id="rfi-post-cancel"
        variant="tertiary"
        size="large"
        onClick={handlePostRFICancelClick}
        style={{ color: '#4d555c', fontSize: '16px', marginRight: '16px' }}
      >
        {translate('buttons:cancel')}
      </Button>
      <Button
        id="rfi-post-confirm"
        variant="primary"
        size="large"
        onClick={handlePostRFIConfirmClick}
        disabled={confirmBtnDisabled()}
      >
        {removingMarkup
          ? translate('buttons:saveChanges')
          : translate('buttons:confirmPlacement')}
      </Button>
    </div>
  );
};

export default PostRFIFooter;
